import { useContext, useEffect, useRef, useState } from 'react';
import Card from 'components/card';
import api from 'ApiConfig';
import studentAssignmentServices from 'ApiService/Placement/studentAssignmentServices';
import companyServices from 'ApiService/Placement/companyServices';
import clientServices from 'ApiService/Placement/clientServices';
import jobRoleServices from 'ApiService/Placement/jobRoleServices';
import jobServices from 'ApiService/Placement/jobServices';
import StudentJobOverviewTable from '../../Student/Jobs/StudentGetPlacementOverview/components/StudentOverviewTable';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';
import { MdDownload } from 'react-icons/md';
import SharedFilesPanel from './SharedFilesPanel';
import { getCollapsedCardStatus } from '../utils/assignmentStatus';

const Index = () => {
  const { state } = useContext(AuthContext);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [filters, setFilters] = useState({
    status: '',
    companyId: '',
    clientId: '',
    departmentId: '',
    studentName: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });
  const [expandedCardId, setExpandedCardId] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedStudentHistory, setSelectedStudentHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [updatingRound, setUpdatingRound] = useState(null);
  const [addingRound, setAddingRound] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Schedule interview state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({ roundNumber: '', scheduledDate: '', scheduledTime: '', notes: '' });
  const [currentAssignmentId, setCurrentAssignmentId] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ interviewId: '', newDate: '', newTime: '', notes: '' });

  // Resend offer state
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [resendAssignmentId, setResendAssignmentId] = useState(null);

  // Reassign job state
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [reassignAssignmentId, setReassignAssignmentId] = useState(null);
  const [reassignExpiry, setReassignExpiry] = useState(24);
  const [reassignReason, setReassignReason] = useState('');

  // Delete assignment
  const [isDeleteAssignModalOpen, setIsDeleteAssignModalOpen] = useState(false);
  const [deleteAssignmentTargetId, setDeleteAssignmentTargetId] = useState(null);

  // Edit assignment (change company/client/department/role/expiry)
  const [isEditAssignModalOpen, setIsEditAssignModalOpen] = useState(false);
  const [editAssignmentTargetId, setEditAssignmentTargetId] = useState(null);
  const [editForm, setEditForm] = useState({ companyId: '', clientId: '', departmentId: '', roleId: '', assignmentExpiry: 24 });
  const [editClients, setEditClients] = useState([]);
  const [editRoles, setEditRoles] = useState([]);
  const [editJdFile, setEditJdFile] = useState(null);
  const [editRemoveJd, setEditRemoveJd] = useState(false);
  const editJdInputRef = useRef(null);

  const roundStatuses = [
    'Pending',
    'Shortlisted',
    'Not Shortlisted',
    'Selected for Next Round',
    'Selected',
    'Not Selected',
    'Rejected',
    'On Hold',
  ];

  const statuses = [
    'Pending',
    'Interview Scheduled',
    'Interview Attended',
    'Selected',
    'Rejected',
    'Offer Accepted',
    'Offer Declined',
    'On Hold',
  ];

  const fetchAssignments = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, ...filters };
      if (activeTab === 'selected') {
        params.status = 'Selected';
      }
      if (dateRange.from) params.dateFrom = dateRange.from;
      if (dateRange.to) params.dateTo = dateRange.to;
      const response = await studentAssignmentServices.getAllAssignments(params);
      if (response.data.success) {
        setAssignments(response.data.assignments);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch assignments');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyServices.getAllCompanies();
      if (response.data.success) {
        setCompanies(response.data.companies.filter((c) => c.isActive));
      }
    } catch (error) {
      console.error('Failed to fetch companies');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/placement/departments');
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  useEffect(() => {
    fetchAssignments();
    fetchCompanies();
    fetchDepartments();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchAssignments(1);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [filters.status, filters.companyId, filters.clientId, filters.departmentId, filters.studentName, activeTab, dateRange.from, dateRange.to]);

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };

    if (name === 'companyId') {
      newFilters.clientId = '';
      if (value) {
        try {
          const { default: clientServices } = await import('ApiService/Placement/clientServices');
          const clientRes = await clientServices.getClientsByCompany(value);
          if (clientRes.data.success)
            setClients(clientRes.data.clients.filter((c) => c.isActive));
        } catch (err) {
          console.error(err);
        }
      } else {
        setClients([]);
      }
    }
    setFilters(newFilters);
  };


  const handleToggleExpand = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  const handleUpdateRoundStatus = async (assignmentId, roundNumber, status) => {
    setUpdatingRound(`${assignmentId}-${roundNumber}`);
    try {
      const response = await jobServices.updateRoundDetails(assignmentId, [
        { roundNumber, status },
      ]);
      if (response.data.success) {
        toast.success(`Round ${roundNumber} updated to "${status}"`);
        fetchAssignments(pagination.page);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update round');
    } finally {
      setUpdatingRound(null);
    }
  };

  const handleAddRound = async (assignment) => {
    setAddingRound(assignment._id);
    try {
      const rounds = assignment.rounds || [];
      const nextRound = Math.max(...rounds.map((r) => r.roundNumber), 0) + 1;
      const response = await jobServices.updateRoundDetails(assignment._id, [
        { roundNumber: nextRound, status: 'Pending', date: new Date().toISOString().split('T')[0] },
      ]);
      if (response.data.success) {
        toast.success(`Round ${nextRound} added`);
        fetchAssignments(pagination.page);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add round');
    } finally {
      setAddingRound(null);
    }
  };

  // Schedule Interview
  const openScheduleModal = (assignmentId, defaultRound) => {
    setCurrentAssignmentId(assignmentId);
    setScheduleData({ roundNumber: defaultRound?.toString() || '', scheduledDate: '', scheduledTime: '', notes: '' });
    setIsScheduleModalOpen(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleData.roundNumber || !scheduleData.scheduledDate) {
      toast.error('Please fill Round Number and Date');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await jobServices.scheduleInterview(currentAssignmentId, {
        roundNumber: Number(scheduleData.roundNumber),
        scheduledDate: scheduleData.scheduledDate,
        scheduledTime: scheduleData.scheduledTime,
        notes: scheduleData.notes,
      });
      if (response.data.success) {
        toast.success('Interview scheduled!');
        setIsScheduleModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to schedule interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel Interview
  const openCancelModal = (assignmentId, interviewId) => {
    setCurrentAssignmentId(assignmentId);
    setSelectedInterviewId(interviewId);
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.cancelInterview(currentAssignmentId, selectedInterviewId);
      if (response.data.success) {
        toast.success('Interview cancelled');
        setIsCancelModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to cancel interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reschedule Interview
  const openRescheduleModal = (assignmentId, interviewId) => {
    setCurrentAssignmentId(assignmentId);
    setRescheduleData({ interviewId, newDate: '', newTime: '', notes: '' });
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleData.newDate) {
      toast.error('Please fill the new date');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await jobServices.rescheduleInterview(currentAssignmentId, {
        interviewId: rescheduleData.interviewId,
        newDate: rescheduleData.newDate,
        newTime: rescheduleData.newTime,
        notes: rescheduleData.notes,
      });
      if (response.data.success) {
        toast.success('Interview rescheduled');
        setIsRescheduleModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reschedule interview');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend Offer
  const openResendModal = (assignmentId) => {
    setResendAssignmentId(assignmentId);
    setIsResendModalOpen(true);
  };

  const handleResendConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.resendOffer(resendAssignmentId);
      if (response.data.success) {
        toast.success('Offer resent to student');
        setIsResendModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to resend offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reassign Job
  const openReassignModal = (assignmentId) => {
    setReassignAssignmentId(assignmentId);
    setReassignExpiry(24);
    setReassignReason('');
    setIsReassignModalOpen(true);
  };

  const handleReassignConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await jobServices.reassignJob(
        reassignAssignmentId,
        reassignExpiry,
        reassignReason
      );
      if (response.data.success) {
        toast.success('Job reassigned to student');
        setIsReassignModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reassign job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Assignment
  const openDeleteAssignModal = (assignmentId) => {
    setDeleteAssignmentTargetId(assignmentId);
    setIsDeleteAssignModalOpen(true);
  };

  const handleDeleteAssignConfirm = async () => {
    try {
      setIsSubmitting(true);
      const response = await studentAssignmentServices.deleteAssignment(deleteAssignmentTargetId);
      if (response.data.success) {
        toast.success('Assignment deleted');
        setIsDeleteAssignModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit Assignment
  const openEditAssignModal = async (assignment) => {
    setEditAssignmentTargetId(assignment._id);
    setEditForm({
      companyId: assignment.companyId?._id || '',
      clientId: assignment.clientId?._id || '',
      departmentId: assignment.departmentId?._id || '',
      roleId: assignment.roleId?._id || '',
      jdSource: assignment.jdSource || (assignment.roleId ? 'role' : 'attachment'),
      jdAttachment: assignment.jdAttachment || null,
      jobTitle: assignment.jobTitle || '',
      assignmentExpiry: assignment.assignmentExpiry || 24,
    });
    setEditJdFile(null);
    setEditRemoveJd(false);
    if (editJdInputRef.current) editJdInputRef.current.value = '';
    setEditClients([]);
    setEditRoles([]);
    setIsEditAssignModalOpen(true);
    try {
      if (assignment.companyId?._id) {
        const clientRes = await clientServices.getClientsByCompany(assignment.companyId._id);
        if (clientRes.data.success) setEditClients(clientRes.data.clients.filter((c) => c.isActive));
      }
      if (assignment.departmentId?._id) {
        const roleRes = await jobRoleServices.getRolesByDepartment(assignment.departmentId._id);
        if (roleRes.data.success) setEditRoles(roleRes.data.roles || roleRes.data.jobRoles || []);
      }
    } catch (error) {
      console.error('Failed to load clients/roles for edit', error);
    }
  };

  const handleEditCompanyChange = async (e) => {
    const companyId = e.target.value;
    setEditForm((prev) => ({ ...prev, companyId, clientId: '' }));
    setEditClients([]);
    if (companyId) {
      try {
        const clientRes = await clientServices.getClientsByCompany(companyId);
        if (clientRes.data.success) setEditClients(clientRes.data.clients.filter((c) => c.isActive));
      } catch (error) {
        console.error('Failed to load clients', error);
      }
    }
  };

  const handleEditDepartmentChange = async (e) => {
    const departmentId = e.target.value;
    setEditForm((prev) => ({ ...prev, departmentId, roleId: '' }));
    setEditRoles([]);
    if (departmentId) {
      try {
        const roleRes = await jobRoleServices.getRolesByDepartment(departmentId);
        if (roleRes.data.success) setEditRoles(roleRes.data.roles || roleRes.data.jobRoles || []);
      } catch (error) {
        console.error('Failed to load roles', error);
      }
    }
  };

  const handleEditAssignConfirm = async () => {
    if (!editForm.companyId || !editForm.departmentId) {
      toast.error('Company and Department are required');
      return;
    }
    if (editForm.jdSource === 'role' && !editForm.roleId) {
      toast.error('Please select a role');
      return;
    }
    // Whatever is left after this edit must still describe the job.
    const willHaveJd = editJdFile || (editForm.jdAttachment && !editRemoveJd);
    if (editForm.jdSource === 'attachment' && !willHaveJd) {
      toast.error('Please attach a JD file');
      return;
    }
    if (editForm.jdSource === 'attachment' && !editForm.jobTitle?.trim()) {
      toast.error('Please enter a role');
      return;
    }
    try {
      setIsSubmitting(true);
      const fd = new FormData();
      fd.append('companyId', editForm.companyId);
      fd.append('clientId', editForm.clientId || '');
      fd.append('departmentId', editForm.departmentId);
      fd.append('jdSource', editForm.jdSource || 'role');
      if (editForm.jdSource === 'role') fd.append('roleId', editForm.roleId);
      if (editForm.jdSource === 'attachment') fd.append('jobTitle', editForm.jobTitle.trim());
      fd.append('removeJd', String(editRemoveJd));
      fd.append('assignmentExpiry', String(editForm.assignmentExpiry));
      if (editJdFile) fd.append('jdFile', editJdFile);

      const response = await studentAssignmentServices.editAssignment(editAssignmentTargetId, fd);
      if (response.data.success) {
        toast.success('Assignment updated');
        setIsEditAssignModalOpen(false);
        fetchAssignments(pagination.page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchStudentHistory = async (studentId) => {
    setLoadingHistory(true);
    setShowHistoryModal(true);
    try {
      const response = await jobServices.getStudentWiseOverview(null, null, null, studentId);
      if (response.data.success) {
        setSelectedStudentHistory(response.data.data);
      } else {
        setSelectedStudentHistory([]);
        toast.error(response?.data?.message || 'Failed to fetch student history');
      }
    } catch (error) {
      setSelectedStudentHistory([]);
      toast.error(error?.response?.data?.message || 'Failed to fetch student history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      Assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'In Progress': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'Interview Scheduled': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Interview Attended': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      Selected: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Not Selected': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      Rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Offer Accepted': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      'Offer Declined': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'On Hold': 'bg-gray-100 text-gray-800 dark:bg-navy-700 dark:text-gray-200',
      'No Rounds': 'bg-gray-100 text-gray-800 dark:bg-navy-700 dark:text-gray-200',
      Accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      Declined: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Auto Declined': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Going To Attend': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Not Going To Attend': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Will Attend': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Cannot Attend': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-navy-700 dark:text-gray-200';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const escapeCSV = (val) => {
    const str = String(val ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const handleDownloadInterviewCSV = () => {
    const student = selectedStudentHistory?.[0];
    if (!student || !student.interviews?.length) {
      toast.error('No interview data to download');
      return;
    }

    const maxRounds = Math.max(
      ...student.interviews.map((i) => i.rounds?.length || 0)
    );

    const baseHeader = [
      'Student Name',
      'Student Email',
      'Student Phone',
      'Branch',
      'Company',
      'Job Title',
      'Location',
      'Job Type',
      'HR Status',
      'Student/Offer Status',
      'Assigned Date',
      'Completed Date',
    ];

    const roundHeader = [];
    for (let n = 1; n <= maxRounds; n++) {
      roundHeader.push(
        `Round ${n} Status`,
        `Round ${n} Student Status`,
        `Round ${n} Date`,
        `Round ${n} Feedback`
      );
    }

    const header = [...baseHeader, ...roundHeader];

    const rows = student.interviews.map((interview) => {
      const row = [
        student.personalInfo?.name || '-',
        student.personalInfo?.email || '-',
        student.personalInfo?.phone || '-',
        student.personalInfo?.branch || '-',
        interview.company || '-',
        interview.jobTitle || '-',
        interview.location || '-',
        interview.jobType || '-',
        interview.overallStatus || '-',
        interview.status || '-',
        formatDate(interview.assignedDate),
        formatDate(interview.completedDate),
      ];
      for (let n = 1; n <= maxRounds; n++) {
        const round = (interview.rounds || []).find((r) => r.roundNumber === n);
        row.push(
          round?.status || '-',
          round?.studentStatus || '-',
          formatDate(round?.date),
          round?.feedback || '-'
        );
      }
      return row;
    });

    const csv = [header, ...rows]
      .map((row) => row.map(escapeCSV).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${(student.personalInfo?.name || 'Student').replace(/\s+/g, '_')}_interview_detail.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Interview overview downloaded successfully.');
  };

  return (
    <div className="mt-4 h-full w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Assignment Overview
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          View and manage all student company assignments
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-6 border-b border-gray-200 dark:border-white/10">
        <button
          onClick={() => { setActiveTab('all'); setDateRange({ from: '', to: '' }); }}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-brand-500 text-brand-500'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          All
        </button>
        <button
          onClick={() => { setActiveTab('selected'); setDateRange({ from: '', to: '' }); }}
          className={`pb-3 text-sm font-medium transition-colors ${
            activeTab === 'selected'
              ? 'border-b-2 border-brand-500 text-brand-500'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Selected Students
        </button>
      </div>

      {/* Filters */}
      <Card extra="mb-6 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Search Student
            </label>
            <input
              type="text"
              name="studentName"
              placeholder="Search by name, email or number..."
              value={filters.studentName}
              onChange={handleFilterChange}
              className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            />
          </div>
          {activeTab === 'all' && (
            <div>
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Filter by Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              >
                <option value="">All Statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Filter by Department
            </label>
            <select
              name="departmentId"
              value={filters.departmentId}
              onChange={handleFilterChange}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Filter by Company
            </label>
            <select
              name="companyId"
              value={filters.companyId}
              onChange={handleFilterChange}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} {c.hasClients ? '(Agency)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Filter by Client
            </label>
            <select
              name="clientId"
              value={filters.clientId}
              onChange={handleFilterChange}
              disabled={!filters.companyId || clients.length === 0}
              className="w-full rounded-xl border border-gray-200 py-2.5 pl-3 pr-3 text-sm outline-none disabled:bg-gray-100 disabled:text-gray-400 dark:!border-white/10 dark:!bg-navy-800 dark:text-white dark:disabled:bg-navy-700"
            >
              <option value="">
                {clients.length > 0 ? 'All Clients' : 'No Clients Available'}
              </option>
              {clients.map((cl) => (
                <option key={cl._id} value={cl._id}>{cl.clientName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              From Date
            </label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              To Date
            </label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-3 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            />
          </div>
        </div>
      </Card>

      {/* Cards */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {assignments.map((assignment) => {
              const isExpanded = expandedCardId === assignment._id;
              const cardStatus = getCollapsedCardStatus(assignment);

              return (
                <Card key={assignment._id} extra="w-full overflow-hidden">
                  {/* Card Header */}
                  <div className="p-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <button
                          onClick={() => fetchStudentHistory(assignment.studentId?._id)}
                          className="cursor-pointer text-left text-lg font-semibold text-brand-500 transition-colors hover:text-brand-600 hover:underline dark:text-brand-400"
                        >
                          {assignment.studentId?.name || '-'}
                        </button>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {assignment.studentId?.email || ''}
                        </p>
                        <p className="mt-1 text-sm font-medium text-navy-700 dark:text-white">
                          {assignment.companyId?.name || '-'}
                          {assignment.roleId?.roleName || assignment.jobTitle || assignment.jdAttachment?.fileName
                            ? ` — ${assignment.roleId?.roleName || assignment.jobTitle || assignment.jdAttachment.fileName}`
                            : ''}
                        </p>
                        {(assignment.clientId?.location || assignment.companyId?.location) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            📍 {assignment.clientId?.location || assignment.companyId?.location}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                          {assignment.clientId?.clientName && (
                            <span>Client: {assignment.clientId.clientName}</span>
                          )}
                          {assignment.departmentId?.name && (
                            <span>Dept: {assignment.departmentId.name}</span>
                          )}
                          <span>Assigned: {formatDate(assignment.createdAt)}</span>
                          <span>By: {assignment.assignedBy?.name || '-'}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-start justify-start gap-2 sm:flex-col sm:items-end">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${cardStatus.bg}`}>
                          {cardStatus.label}
                        </span>
                        <span className={`rounded-md px-3 py-1.5 text-xs font-medium ${cardStatus.descBg}`}>
                          {cardStatus.desc}
                          {assignment.revertReason && <div className="mt-0.5 text-[11px] font-normal opacity-80">Reason: {assignment.revertReason}</div>}
                        </span>
                        {(assignment.studentJobResponse === 'Cannot Attend' || assignment.studentRevert === 'Auto Declined') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openReassignModal(assignment._id); }}
                            disabled={isSubmitting}
                            className="rounded bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                          >
                            Reassign Job
                          </button>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditAssignModal(assignment); }}
                            disabled={isSubmitting}
                            className="rounded-full border border-blue-300 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-navy-800"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteAssignModal(assignment._id); }}
                            disabled={isSubmitting}
                            className="rounded-full border border-red-300 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-navy-800"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={() => handleToggleExpand(assignment._id)}
                        className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                      >
                        {isExpanded ? '▲ Hide Details' : '▼ View Details'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Section */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 dark:border-white/10">
                      <div className="space-y-6">
                        {/* Student Info */}
                        <div>
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                            Student Info
                          </h4>
                          <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                            <div><span className="font-medium text-gray-500">Name:</span> {assignment.studentId?.name || '-'}</div>
                            <div><span className="font-medium text-gray-500">Email:</span> {assignment.studentId?.email || '-'}</div>
                            <div><span className="font-medium text-gray-500">Company:</span> {assignment.companyId?.name || '-'}</div>
                            <div><span className="font-medium text-gray-500">Client:</span> {assignment.clientId?.clientName || '-'}</div>
                            <div><span className="font-medium text-gray-500">Department:</span> {assignment.departmentId?.name || '-'}</div>
                            <div><span className="font-medium text-gray-500">Role:</span> {assignment.roleId?.roleName || assignment.jobTitle || assignment.jdAttachment?.fileName || '-'}</div>
                            <div><span className="font-medium text-gray-500">Location:</span> {assignment.clientId?.location || assignment.companyId?.location || '-'}</div>
                            <div><span className="font-medium text-gray-500">Assigned By:</span> {assignment.assignedBy?.name || '-'}</div>
                            <div><span className="font-medium text-gray-500">Date:</span> {formatDate(assignment.createdAt)}</div>
                            {assignment.jdAttachment?.filePath && (
                              <div className="col-span-2">
                                <span className="font-medium text-gray-500">JD:</span>{' '}
                                <a
                                  href={`${process.env.REACT_APP_API_BASE_URL}${assignment.jdAttachment.filePath}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-brand-600 hover:underline dark:text-brand-400"
                                >
                                  {assignment.jdAttachment.fileName}
                                </a>
                              </div>
                            )}
                            {assignment.notes && (
                              <div className="col-span-2"><span className="font-medium text-gray-500">Notes:</span> {assignment.notes}</div>
                            )}
                          </div>
                        </div>

                        {/* Interview Rounds */}
                        {assignment.rounds?.length > 0 && (
                          <div>
                            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                              Interview Rounds
                            </h4>
                            <div className="space-y-3">
                              {assignment.rounds.map((round) => {
                                const isUpdating = updatingRound === `${assignment._id}-${round.roundNumber}`;
                                const roundsList = assignment.rounds || [];
                                const isLastRound = roundsList.length === 0 || roundsList[roundsList.length - 1].roundNumber === round.roundNumber;
                                const isRoundLocked = !isLastRound;
                                return (
                                  <div key={round.roundNumber} className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                      <div>
                                        <span className="text-sm font-medium dark:text-white">
                                          Round {round.roundNumber}
                                        </span>
                                        <span className="ml-2 text-xs text-gray-500">
                                          ({formatDate(round.date)})
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(round.studentStatus)}`}>
                                          Student: {round.studentStatus}
                                        </span>
                                        {(round.status === 'Pending' || round.status === 'Shortlisted') && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); openScheduleModal(assignment._id, round.roundNumber); }}
                                            disabled={isSubmitting}
                                            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                          >
                                            Schedule
                                          </button>
                                        )}
                                        {assignment.scheduledInterviews?.filter(i => i.roundNumber === round.roundNumber).map(interview => (
                                          interview.status !== 'Cancelled' ? (
                                            <div key={interview._id} className="flex gap-2">
                                              <button
                                                onClick={(e) => { e.stopPropagation(); openCancelModal(assignment._id, interview._id); }}
                                                disabled={isSubmitting}
                                                className="rounded bg-red-500 px-3 py-1 text-xs font-medium text-white hover:bg-red-600 disabled:opacity-50"
                                              >
                                                Cancel
                                              </button>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); openRescheduleModal(assignment._id, interview._id); }}
                                                disabled={isSubmitting}
                                                className="rounded bg-amber-500 px-3 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                                              >
                                                Reschedule
                                              </button>
                                            </div>
                                          ) : (
                                            <button
                                              key={interview._id}
                                              onClick={(e) => { e.stopPropagation(); openScheduleModal(assignment._id, interview.roundNumber); }}
                                              disabled={isSubmitting}
                                              className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                            >
                                              Schedule Again
                                            </button>
                                          )
                                        ))}
                                      </div>
                                    </div>
                                     <div className="flex flex-wrap gap-1.5">
                                       {roundStatuses.map((s) => {
                                         if (s === 'Shortlisted' || s === 'Not Shortlisted') {
                                           return null;
                                         }
                                         const isCurrentStatus = round.status === s;
                                         return (
                                           <button
                                             key={s}
                                             onClick={() => handleUpdateRoundStatus(assignment._id, round.roundNumber, s)}
                                            disabled={isUpdating || isRoundLocked}
                                            className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                              isCurrentStatus
                                                ? 'bg-brand-500 text-white ring-2 ring-brand-300'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                          >
                                            {s}
                                          </button>
                                         );
                                       })}
                                     </div>
                                      {round.roundNumber === 1 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {(() => {
                                            const isShortlisted = round.status === 'Shortlisted';
                                            const isNotShortlisted = round.status === 'Not Shortlisted';
                                            return (
                                              <>
                                                <button
                                                  onClick={() => handleUpdateRoundStatus(assignment._id, round.roundNumber, 'Shortlisted')}
                                                  disabled={isUpdating || isRoundLocked}
                                                  className={`rounded-full px-3 py-1 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${isShortlisted ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'}`}
                                                >
                                                  Shortlisted
                                                </button>
                                                <button
                                                  onClick={() => handleUpdateRoundStatus(assignment._id, round.roundNumber, 'Not Shortlisted')}
                                                  disabled={isUpdating || isRoundLocked}
                                                  className={`rounded-full px-3 py-1 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${isNotShortlisted ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'}`}
                                                >
                                                  Not Shortlisted
                                                </button>
                                              </>
                                            );
                                          })()}
                                        </div>
                                      )}
                                     {round.feedback && (
                                       <p className="mt-1.5 text-xs italic text-gray-500">
                                         "{round.feedback}"
                                      </p>
                                    )}
                                    {(() => {
                                      const interview = assignment.scheduledInterviews?.find(i => i.roundNumber === round.roundNumber && i.notes);
                                      return interview ? <p className="mt-1 text-xs text-gray-500">📌 {interview.notes}</p> : null;
                                    })()}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Offer Section */}
                        {assignment.overallStatus === 'Selected' && (
                          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-navy-800">
                            <h4 className="mb-2 font-semibold text-green-700 dark:text-green-300">Offer Section</h4>
                            <p className="mb-2 text-sm">
                              <strong>Student Response:</strong>{' '}
                              <span className={assignment.studentRevert === 'Accepted' ? 'text-green-600 font-medium' : assignment.studentRevert === 'Declined' || assignment.studentRevert === 'Auto Declined' ? 'text-red-600 font-medium' : 'text-yellow-600 font-medium'}>
                                {assignment.studentRevert || 'Pending'}
                              </span>
                              {assignment.revertReason && <span className="ml-2 text-xs text-gray-500">— {assignment.revertReason}</span>}
                            </p>
                            {(assignment.studentRevert === 'Declined' || assignment.studentRevert === 'Auto Declined') && !assignment.offerResendAllowed && (
                              <button onClick={() => openResendModal(assignment._id)} disabled={isSubmitting} className="rounded bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50">
                                Resend Offer
                              </button>
                            )}
                            {assignment.offerResendAllowed && <p className="mt-1 text-xs text-green-600">Offer has been resent to student.</p>}
                          </div>
                        )}

                        {/* Shared Files */}
                        <div>
                          <SharedFilesPanel assignmentId={assignment._id} />
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {assignments.length === 0 && (
            <div className="flex h-40 items-center justify-center">
              <p className="text-gray-500">No assignments found.</p>
            </div>
          )}

          {pagination.pages > 1 && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => fetchAssignments(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchAssignments(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Schedule Interview Modal */}
      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Schedule Interview</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Round Number</label>
                <input type="number" value={scheduleData.roundNumber} onChange={(e) => setScheduleData(p => ({ ...p, roundNumber: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Date</label>
                <input type="date" value={scheduleData.scheduledDate} onChange={(e) => setScheduleData(p => ({ ...p, scheduledDate: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Time (optional)</label>
                <input type="time" value={scheduleData.scheduledTime} onChange={(e) => setScheduleData(p => ({ ...p, scheduledTime: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Notes (optional)</label>
                <textarea value={scheduleData.notes} onChange={(e) => setScheduleData(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="Any additional notes..." />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsScheduleModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleScheduleConfirm} disabled={isSubmitting} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Scheduling...' : 'Schedule'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Interview Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Cancel Interview</h2>
            <p className="mb-4 text-sm text-gray-600">Are you sure you want to cancel this interview? The student will be notified.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsCancelModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Go Back</button>
              <button onClick={handleCancelConfirm} disabled={isSubmitting} className="rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50">{isSubmitting ? 'Cancelling...' : 'Yes, Cancel'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Interview Modal */}
      {isRescheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Reschedule Interview</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Date</label>
                <input type="date" value={rescheduleData.newDate} onChange={(e) => setRescheduleData(p => ({ ...p, newDate: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Time (optional)</label>
                <input type="time" value={rescheduleData.newTime} onChange={(e) => setRescheduleData(p => ({ ...p, newTime: e.target.value }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Notes (optional)</label>
                <textarea value={rescheduleData.notes} onChange={(e) => setRescheduleData(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="Any additional notes..." />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsRescheduleModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleRescheduleConfirm} disabled={isSubmitting} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Rescheduling...' : 'Reschedule'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Offer Modal */}
      {isResendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Resend Offer</h2>
            <p className="mb-4 text-sm text-gray-600">This will reset the student's offer status to pending and restart the 24-hour timer. Proceed?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsResendModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleResendConfirm} disabled={isSubmitting} className="rounded-full bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50">{isSubmitting ? 'Resending...' : 'Yes, Resend Offer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Job Modal */}
      {isReassignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Reassign Job</h2>
            <p className="mb-4 text-sm text-gray-600">
              This will reset the student's response status to pending and restart the response timer. The student will be notified again.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">New Response Deadline</label>
                <select value={reassignExpiry} onChange={(e) => setReassignExpiry(Number(e.target.value))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value={24}>24 Hours (Default)</option>
                  <option value={12}>12 Hours</option>
                  <option value={6}>6 Hours</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Reason (optional)</label>
                <textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} rows={2} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white" placeholder="e.g., Student could not apply within the deadline" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsReassignModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleReassignConfirm} disabled={isSubmitting} className="rounded-full bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50">{isSubmitting ? 'Reassigning...' : 'Reassign Job'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Assignment Modal */}
      {isDeleteAssignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Delete Assignment</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">This will permanently remove this assignment from the student's account. This cannot be undone. Proceed?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsDeleteAssignModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleDeleteAssignConfirm} disabled={isSubmitting} className="rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50">{isSubmitting ? 'Deleting...' : 'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Assignment Modal */}
      {isEditAssignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-4 text-lg font-semibold dark:text-white">Edit Assignment</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Company</label>
                <select value={editForm.companyId} onChange={handleEditCompanyChange} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value="">Select a company</option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Client (optional)</label>
                <select value={editForm.clientId} onChange={(e) => setEditForm((p) => ({ ...p, clientId: e.target.value }))} disabled={editClients.length === 0} className="w-full rounded border p-2 text-sm disabled:bg-gray-100 dark:bg-navy-800 dark:text-white dark:disabled:bg-navy-700">
                  <option value="">{editClients.length > 0 ? 'No Client' : 'No Clients Available'}</option>
                  {editClients.map((cl) => (
                    <option key={cl._id} value={cl._id}>{cl.clientName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Department</label>
                <select value={editForm.departmentId} onChange={handleEditDepartmentChange} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value="">Select a department</option>
                  {departments.map((d) => (
                    <option key={d._id} value={d._id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">JD Source</label>
                <div className="inline-flex w-full rounded border p-1 dark:bg-navy-800">
                  {[
                    { key: 'role', label: 'Select Role' },
                    { key: 'attachment', label: 'Attach JD' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setEditForm((p) => ({ ...p, jdSource: opt.key, roleId: opt.key === 'attachment' ? '' : p.roleId }))}
                      className={`flex-1 rounded px-3 py-1.5 text-sm font-medium ${editForm.jdSource === opt.key ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-300'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {editForm.jdSource === 'role' && (
                <div>
                  <label className="mb-1 block text-sm font-medium dark:text-white">Role</label>
                  <select value={editForm.roleId} onChange={(e) => setEditForm((p) => ({ ...p, roleId: e.target.value }))} disabled={editRoles.length === 0} className="w-full rounded border p-2 text-sm disabled:bg-gray-100 dark:bg-navy-800 dark:text-white dark:disabled:bg-navy-700">
                    <option value="">Select a role</option>
                    {editRoles.map((r) => (
                      <option key={r._id} value={r._id}>{r.roleName}</option>
                    ))}
                  </select>
                </div>
              )}
              {editForm.jdSource === 'attachment' && (
                <div>
                  <label className="mb-1 block text-sm font-medium dark:text-white">Role *</label>
                  <input
                    type="text"
                    value={editForm.jobTitle}
                    onChange={(e) => setEditForm((p) => ({ ...p, jobTitle: e.target.value }))}
                    placeholder="e.g. Backend Developer Intern"
                    className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white"
                  />
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">
                  {editForm.jdSource === 'attachment' ? 'JD File *' : 'JD File (optional)'}
                </label>
                <input
                  ref={editJdInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size > 10 * 1024 * 1024) {
                      toast.error('JD file must be 10 MB or smaller.');
                      e.target.value = '';
                      return;
                    }
                    setEditJdFile(file || null);
                    if (file) setEditRemoveJd(false);
                  }}
                />
                {editJdFile ? (
                  <div className="flex items-center gap-2 rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                    <span className="truncate">{editJdFile.name}</span>
                    <button type="button" onClick={() => { setEditJdFile(null); if (editJdInputRef.current) editJdInputRef.current.value = ''; }} className="ml-auto shrink-0 text-gray-400 hover:text-red-500">&#10005;</button>
                  </div>
                ) : editForm.jdAttachment?.filePath && !editRemoveJd ? (
                  <div className="flex items-center gap-2 rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                    <a href={`${process.env.REACT_APP_API_BASE_URL}${editForm.jdAttachment.filePath}`} target="_blank" rel="noreferrer" className="truncate text-blue-600 hover:underline dark:text-blue-400">
                      {editForm.jdAttachment.fileName}
                    </a>
                    <button type="button" onClick={() => editJdInputRef.current?.click()} className="ml-auto shrink-0 text-xs font-medium text-blue-600 hover:underline">Replace</button>
                    <button type="button" onClick={() => setEditRemoveJd(true)} className="shrink-0 text-xs font-medium text-red-500 hover:underline">Remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => editJdInputRef.current?.click()} className="w-full rounded border p-2 text-left text-sm text-gray-500 dark:bg-navy-800">
                    Choose file
                  </button>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium dark:text-white">Response Deadline</label>
                <select value={editForm.assignmentExpiry} onChange={(e) => setEditForm((p) => ({ ...p, assignmentExpiry: Number(e.target.value) }))} className="w-full rounded border p-2 text-sm dark:bg-navy-800 dark:text-white">
                  <option value={24}>24 Hours</option>
                  <option value={12}>12 Hours</option>
                  <option value={6}>6 Hours</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsEditAssignModalOpen(false)} disabled={isSubmitting} className="rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={handleEditAssignConfirm} disabled={isSubmitting || !editForm.companyId || !editForm.departmentId || (editForm.jdSource === 'role' && !editForm.roleId)} className="rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Student History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-y-auto rounded-xl bg-white p-6 dark:bg-navy-800">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                Student Assignment History
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                {selectedStudentHistory?.[0]?.resume?.filePath && (
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL}/${selectedStudentHistory[0].resume.filePath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                    title="View student uploaded resume"
                  >
                    View Resume
                  </a>
                )}
                {selectedStudentHistory?.[0]?.fresherResume?.filePath && (
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL}/${selectedStudentHistory[0].fresherResume.filePath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                    title="View student fresher resume"
                  >
                    Fresher Resume
                  </a>
                )}
                {selectedStudentHistory?.[0]?.experienceResume?.filePath && (
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL}/${selectedStudentHistory[0].experienceResume.filePath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-600"
                    title="View student experience resume"
                  >
                    Experience Resume
                  </a>
                )}
                <button
                  onClick={handleDownloadInterviewCSV}
                  disabled={!selectedStudentHistory?.[0]?.interviews?.length}
                  className="flex items-center gap-1 rounded bg-green-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Download all interview overview as Excel"
                >
                  <MdDownload className="h-4 w-4" />
                  Download Excel
                </button>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-500 transition-colors hover:text-red-500"
                  title="Close"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="h-full max-h-[70vh] flex-1 overflow-auto rounded-lg border border-gray-200 dark:border-navy-700">
              {selectedStudentHistory && selectedStudentHistory.length > 0 ? (
                <StudentJobOverviewTable data={selectedStudentHistory[0]} isLoading={loadingHistory} />
              ) : (
                <div className="flex h-40 items-center justify-center text-gray-500">
                  {loadingHistory ? 'Loading...' : 'No placement details found.'}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="rounded-xl bg-gray-300 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
